import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { db } from '@/src/lib/firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Users, MessageSquare, Activity, CheckCircle2, XCircle, Clock, ExternalLink, Zap, Target, Mail, Send, Bell, Linkedin, Phone, Sparkles, AlertTriangle, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/src/lib/utils';
import { GoogleGenAI, Type } from "@google/genai";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company: string;
  businessName?: string;
  interest?: string;
  message: string;
  status: 'New' | 'Contacted' | 'Replied' | 'Call Booked' | 'Call Completed' | 'Audit Sold' | 'Upsell Closed' | 'Closed' | 'Invited' | 'Requested Info';
  revenue?: number;
  stageHistory?: Record<string, string>;
  createdAt: any;
  intentTier?: 'HIGH' | 'MEDIUM' | 'LOW';
  urgency?: 'high' | 'medium' | 'low';
  aiSummary?: string;
  recommendedAction?: string;
  outreach?: {
    emailReply: string;
    founderAlert: string;
    day1FollowUp: string;
    day3FollowUp: string;
    linkedInMessage: string;
    highResponse: string;
    mediumResponse: string;
    lowResponse: string;
    talkingPoints: string[];
  };
}

interface Audit {
  id: string;
  leadId: string;
  clientName: string;
  riskScore: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  status: 'Draft' | 'Delivered';
  generatedAt: string;
}

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'leads' | 'audits' | 'pipeline'>('leads');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [pipelineStats, setPipelineStats] = useState<any>(null);
  const [optimizationInsights, setOptimizationInsights] = useState<any>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [isGeneratingAudit, setIsGeneratingAudit] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<string | null>(null);
  const [isDeliveringAudit, setIsDeliveringAudit] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    highIntent: 0,
    conversionRate: 0
  });

  const [filter, setFilter] = useState<'all' | 'new' | 'high' | 'medium' | 'low'>('all');

  const generateOptimizationInsights = async (stats: any) => {
    if (!stats || isOptimizing) return;
    setIsOptimizing(true);
    
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY! });
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Analyze these cybersecurity consulting pipeline stats and identify the biggest bottleneck. Suggest 3 highly practical, simple improvements for the DMV (DC, Virginia, Maryland) area. Focus on: outreach messaging, response timing, and call script adjustments.
        
        Stats:
        - Total Leads: ${stats.totalLeads}
        - Lead -> Reply Rate: ${stats.replyRate}%
        - Reply -> Call Rate: ${stats.callsBooked > 0 ? Math.round((stats.callsBooked / stats.replied) * 100) : 0}%
        - Call -> Client Rate: ${stats.callToClientRate}%
        - Client -> Upsell Rate: ${stats.clientToUpsellRate}%
        - Revenue: $${stats.totalRevenue}`,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              bottleneck: { type: Type.STRING },
              suggestions: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    title: { type: Type.STRING },
                    action: { type: Type.STRING },
                    impact: { type: Type.STRING }
                  }
                }
              },
              priority: { type: Type.STRING, enum: ["HIGH", "MEDIUM", "LOW"] }
            }
          }
        }
      });
      
      const insights = JSON.parse(response.text);
      setOptimizationInsights(insights);
    } catch (err) {
      console.error("AI Optimization Error:", err);
    } finally {
      setIsOptimizing(false);
    }
  };

  const fetchPipelineStats = async () => {
    try {
      const res = await fetch('/api/pipeline-stats');
      const data = await res.json();
      setPipelineStats(data);
    } catch (err) {
      console.error("Failed to fetch pipeline stats:", err);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
    
    const unsubscribeLeads = onSnapshot(q, (snapshot) => {
      const leadsData: Lead[] = [];
      let newCount = 0;
      let highIntentCount = 0;
      let bookedCount = 0;

      snapshot.forEach((doc) => {
        const data = doc.data() as any;
        const lead = { 
          id: doc.id, 
          ...data,
          status: data.status ? (data.status.charAt(0).toUpperCase() + data.status.slice(1)) : 'New',
          intentTier: data.intentTier || data.qualification?.tier?.toUpperCase(),
          outreach: data.outreach || data.communication
        } as Lead;
        leadsData.push(lead);
        
        if (lead.status === 'New') newCount++;
        if (lead.intentTier === 'HIGH') highIntentCount++;
        if (lead.status === 'Booked') bookedCount++;
      });

      setLeads(leadsData);
      setStats({
        total: leadsData.length,
        new: newCount,
        highIntent: highIntentCount,
        conversionRate: leadsData.length > 0 ? Math.round((bookedCount / leadsData.length) * 100) : 0
      });
      setLoading(false);
    });

    fetchPipelineStats();

    const auditsQuery = query(collection(db, 'audits'), orderBy('generatedAt', 'desc'));
    const unsubscribeAudits = onSnapshot(auditsQuery, (snapshot) => {
      const auditsData: Audit[] = [];
      snapshot.forEach((doc) => {
        auditsData.push({ id: doc.id, ...doc.data() } as Audit);
      });
      setAudits(auditsData);
    });

    return () => {
      unsubscribeLeads();
      unsubscribeAudits();
    };
  }, []);

  const triggerAutomation = async (lead: Lead) => {
    setIsGenerating(lead.id);
    try {
      await fetch('/api/automate-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lead)
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsGenerating(null);
    }
  };

  const generateAudit = async (leadId: string) => {
    setIsGeneratingAudit(leadId);
    try {
      const res = await fetch('/api/generate-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId })
      }).then(r => r.json());
      
      if (res.id) {
        alert("Cybersecurity Audit generated successfully!");
        setActiveTab('audits');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingAudit(null);
    }
  };

  const deliverAudit = async (auditId: string) => {
    setIsDeliveringAudit(auditId);
    try {
      const res = await fetch('/api/deliver-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auditId })
      }).then(r => r.json());
      
      if (res.success) {
        alert("Audit delivered to client successfully!");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeliveringAudit(null);
    }
  };

  const sendResponse = async (leadId: string, templateType: string, content: string) => {
    setIsSending(leadId);
    try {
      await fetch('/api/send-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId, templateType, content })
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(null);
    }
  };

  const sendCallInvite = async (leadId: string) => {
    try {
      await fetch('/api/send-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadId })
      });
      alert("Call invitation sent!");
    } catch (error) {
      console.error(error);
    }
  };

  const updateLeadStatus = async (leadId: string, status: string, revenue?: number, outcome?: string) => {
    try {
      await fetch(`/api/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, revenue, outcome })
      });
      // Update local state for immediate feedback
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: status as any, revenue } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => prev ? { ...prev, status: status as any, revenue } : null);
      }
      // Refresh stats
      fetchPipelineStats();
    } catch (error) {
      console.error(error);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filter === 'all') return true;
    if (filter === 'new') return lead.status === 'New';
    if (filter === 'high') return lead.intentTier === 'HIGH';
    if (filter === 'medium') return lead.intentTier === 'MEDIUM';
    if (filter === 'low') return lead.intentTier === 'LOW';
    return true;
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-32 space-y-8">
        <Skeleton className="h-10 w-48 bg-white/5" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 bg-white/5 rounded-2xl" />)}
        </div>
        <Skeleton className="h-[400px] w-full bg-white/5 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8 space-y-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em]">
              Internal Lead Management
            </div>
            <h1 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Automation <span className="text-accent">CRM</span></h1>
            <p className="text-muted-foreground text-sm font-normal">Real-time business automation and lead qualification pipeline.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="border-white/10 text-[10px] uppercase font-bold tracking-widest h-10 px-6 rounded-xl">Export CSV</Button>
            <Button className="bg-accent text-background text-[10px] uppercase font-bold tracking-widest h-10 px-6 rounded-xl">System Settings</Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-white/5 no-print">
          <button 
            onClick={() => setActiveTab('leads')}
            className={cn(
              "px-8 py-4 text-[10px] uppercase tracking-[0.2em] font-bold transition-all border-b-2",
              activeTab === 'leads' ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Client Leads
          </button>
          <button 
            onClick={() => setActiveTab('audits')}
            className={cn(
              "px-8 py-4 text-[10px] uppercase tracking-[0.2em] font-bold transition-all border-b-2",
              activeTab === 'audits' ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Audit Delivery
          </button>
          <button 
            onClick={() => setActiveTab('pipeline')}
            className={cn(
              "px-8 py-4 text-[10px] uppercase tracking-[0.2em] font-bold transition-all border-b-2",
              activeTab === 'pipeline' ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Pipeline Stats
          </button>
        </div>

        {activeTab === 'leads' ? (
          <div className="space-y-10">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: "Total Inquiries", value: stats.total, icon: Users, color: "text-blue-400" },
                { label: "New Leads", value: stats.new, icon: Activity, color: "text-accent" },
                { label: "High Intent (AI)", value: stats.highIntent, icon: Zap, color: "text-yellow-400" },
                { label: "Conversion Rate", value: `${stats.conversionRate}%`, icon: Target, color: "text-green-400" }
              ].map((stat, i) => (
                <Card key={i} className="glass-card border-white/5 bg-white/[0.01] rounded-2xl">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${stat.color}`}>
                        <stat.icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-3xl font-display font-bold">{stat.value}</p>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{stat.label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-display font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-accent" /> Recent Activity</h2>
                <select 
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-1 text-[10px] font-bold text-foreground outline-none"
                >
                  <option value="all">All Leads</option>
                  <option value="new">New</option>
                  <option value="high">High Intent</option>
                </select>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {filteredLeads.map((lead) => (
                  <motion.div
                    key={lead.id}
                    className={`glass-card border-white/5 bg-white/[0.01] rounded-2xl p-6 group ${lead.intentTier === 'HIGH' ? 'ring-2 ring-accent' : ''}`}
                  >
                    <div className="flex flex-col lg:flex-row gap-8 lg:items-center">
                      <div className="lg:w-1/4 space-y-3">
                        <h3 className="text-lg font-display font-bold">{lead.name}</h3>
                        <p className="text-xs text-muted-foreground">{lead.email}</p>
                      </div>

                      <div className="flex-1 space-y-4">
                        <div className="glass-card p-4 rounded-xl bg-white/[0.02] border-white/5">
                          <p className="text-[11px] italic leading-relaxed text-foreground/90">"{lead.message}"</p>
                          {lead.aiSummary && (
                            <div className="mt-4 pt-4 border-t border-white/5 space-y-1">
                              <p className="text-[9px] font-bold uppercase text-accent">AI Summary</p>
                              <p className="text-[10px] text-muted-foreground">{lead.aiSummary}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="lg:w-1/4 flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                           <Button 
                            className="bg-accent text-background text-[9px] uppercase font-black tracking-widest h-10 px-4 rounded-xl"
                            onClick={() => sendCallInvite(lead.id)}
                          >
                            <Send className="w-3.5 h-3.5 mr-2" /> Send Call Invite
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => generateAudit(lead.id)} 
                            disabled={isGeneratingAudit === lead.id} 
                            className="h-10 text-[9px] uppercase tracking-widest font-black text-accent bg-accent/5 border-accent/20 rounded-xl"
                          >
                            {isGeneratingAudit === lead.id ? 'Generating...' : '🛡️ Generate Audit'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-10 text-[9px] uppercase tracking-widest font-bold"
                            onClick={() => setSelectedLead(lead)}
                          >
                            Outreach Suite
                          </Button>
                        </div>
                        <Badge variant="outline" className="justify-center border-white/10">{lead.status}</Badge>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        ) : activeTab === 'audits' ? (
          <div className="space-y-6">
            <h2 className="text-xl font-display font-bold tracking-tight">Active Audit Pipeline</h2>
            {audits.length === 0 ? (
              <div className="glass-card p-20 rounded-3xl text-center border-dashed border-white/10">
                <p className="text-muted-foreground">No audit reports generated yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {audits.map((audit) => (
                  <motion.div 
                    key={audit.id}
                    className="glass-card p-8 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-8 border-white/5 shadow-xl"
                  >
                    <div className="flex items-center gap-6">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-display font-bold text-2xl border bg-accent/5 text-accent">
                        {audit.riskScore}
                      </div>
                      <div>
                        <h3 className="text-xl font-display font-bold">{audit.clientName}</h3>
                        <p className="text-[10px] uppercase text-muted-foreground">Rating: {audit.riskLevel}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className={audit.status === 'Delivered' ? "text-green-500 border-green-500/20" : "text-yellow-500 border-yellow-500/20"}>
                        {audit.status}
                      </Badge>
                      <Button asChild variant="outline" size="sm">
                        <a href={`/audit/${audit.id}`} target="_blank" rel="noopener noreferrer">Live Preview</a>
                      </Button>
                      <Button asChild variant="outline" size="sm" className="border-accent/20 text-accent hover:bg-accent/5">
                        <Link to={`/admin/consultation/${audit.id}`}>
                           <MessageSquare className="w-3.5 h-3.5 mr-2" /> Playbook
                        </Link>
                      </Button>
                      {audit.status === 'Draft' && (
                        <Button 
                          onClick={() => deliverAudit(audit.id)} 
                          disabled={isDeliveringAudit === audit.id}
                          className="bg-accent text-background"
                        >
                          {isDeliveringAudit === audit.id ? 'Sending...' : 'Deliver'}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-10">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-display font-bold tracking-tight">Business Conversion Analytics</h2>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-white/5 border-white/10 px-4 py-1">Daily: {pipelineStats?.dailyCount || 0}</Badge>
                <Badge variant="outline" className="bg-white/5 border-white/10 px-4 py-1">Weekly: {pipelineStats?.weeklyCount || 0}</Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: "Total Revenue", value: `$${(pipelineStats?.totalRevenue || 0).toLocaleString()}`, icon: Shield, color: "text-accent" },
                { label: "Audits Sold", value: pipelineStats?.auditsSold || 0, icon: CheckCircle2, color: "text-green-400" },
                { label: "Calls Completed", value: pipelineStats?.callsCompleted || 0, icon: Phone, color: "text-blue-400" },
                { label: "Upsells Closed", value: pipelineStats?.upsellsClosed || 0, icon: Target, color: "text-purple-400" }
              ].map((stat, i) => (
                <Card key={i} className="glass-card border-white/5 bg-white/[0.01] rounded-3xl">
                  <CardContent className="p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div className={`w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center ${stat.color}`}>
                        <stat.icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-4xl font-display font-bold">{stat.value}</p>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-black">{stat.label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-3">
                <Card className="glass-card border-accent/20 bg-accent/[0.02] rounded-[2.5rem] overflow-hidden">
                  <CardContent className="p-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-accent">
                          <Sparkles className="w-5 h-5" />
                          <h3 className="text-xl font-display font-bold">AI Strategic Optimization</h3>
                        </div>
                        <p className="text-xs text-muted-foreground">Practical, data-driven improvements for your consulting pipeline.</p>
                      </div>
                      <Button 
                        onClick={() => generateOptimizationInsights(pipelineStats)}
                        disabled={isOptimizing || !pipelineStats}
                        className="bg-accent text-background rounded-2xl px-8 h-12 text-xs font-black uppercase tracking-widest"
                      >
                        {isOptimizing ? 'Analyzing Data...' : 'Analyze & Optimize Funnel'}
                      </Button>
                    </div>

                    {optimizationInsights ? (
                      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="p-6 bg-white/5 rounded-3xl border border-white/5 space-y-3">
                          <div className="flex items-center gap-2 text-yellow-400">
                            <AlertTriangle className="w-4 h-4" />
                            <p className="text-[10px] uppercase font-bold tracking-widest">Identified Bottleneck</p>
                          </div>
                          <p className="text-lg leading-relaxed">{optimizationInsights.bottleneck}</p>
                          <div className="flex items-center gap-2 pt-2">
                            <Badge className={optimizationInsights.priority === 'HIGH' ? "bg-red-500/10 text-red-500 border-red-500/20" : "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"}>
                              Priority: {optimizationInsights.priority}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {optimizationInsights.suggestions.map((s: any, i: number) => (
                            <div key={i} className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] space-y-4">
                              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                                <TrendingUp className="w-5 h-5" />
                              </div>
                              <h4 className="font-bold text-sm">{s.title}</h4>
                              <p className="text-xs text-muted-foreground leading-relaxed italic">"{s.action}"</p>
                              <p className="text-[10px] text-accent font-bold uppercase pt-2">Impact: {s.impact}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="p-20 text-center border border-dashed border-white/10 rounded-3xl">
                        <p className="text-muted-foreground max-w-sm mx-auto">Click "Analyze & Optimize Funnel" to generate AI-powered strategies based on your current pipeline performance.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-2 glass-card p-10 rounded-[2.5rem] border-white/5 bg-white/[0.01]">
                <h3 className="text-lg font-bold mb-8 uppercase tracking-widest text-muted-foreground">Funnel Efficiency</h3>
                <div className="space-y-12">
                  {[
                    { label: "Lead-to-Reply Conversion", rate: pipelineStats?.replyRate || 0, desc: "Outreach engagement effectiveness" },
                    { label: "Lead-to-Call Conversion", rate: pipelineStats?.leadToCallRate || 0, desc: "Primary bridge effectiveness" },
                    { label: "Call-to-Client Conversion", rate: pipelineStats?.callToClientRate || 0, desc: "Sales closing effectiveness" },
                    { label: "Client-to-Upsell Conversion", rate: pipelineStats?.clientToUpsellRate || 0, desc: "Service expansion effectiveness" }
                  ].map((conv, i) => (
                    <div key={i} className="space-y-4">
                      <div className="flex justify-between items-end">
                        <div className="space-y-1">
                          <p className="font-bold">{conv.label}</p>
                          <p className="text-xs text-muted-foreground">{conv.desc}</p>
                        </div>
                        <p className="text-2xl font-display font-bold text-accent">{conv.rate}%</p>
                      </div>
                      <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${conv.rate}%` }}
                          transition={{ duration: 1, delay: i * 0.2 }}
                          className="h-full bg-accent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-10 rounded-[2.5rem] border-accent/20 bg-accent/[0.02]">
                <h3 className="text-lg font-bold mb-6 text-accent">Pipeline Health</h3>
                <div className="space-y-6">
                  <div className="p-6 bg-white/5 rounded-2xl space-y-2">
                    <p className="text-[10px] uppercase font-bold text-muted-foreground">Leads Contacted</p>
                    <p className="text-2xl font-bold">{pipelineStats?.contacted || 0}</p>
                  </div>
                  <div className="p-6 bg-white/5 rounded-2xl space-y-2">
                    <p className="text-[10px] uppercase font-bold text-muted-foreground">Calls Booked</p>
                    <p className="text-2xl font-bold">{pipelineStats?.callsBooked || 0}</p>
                  </div>
                  <div className="mt-8 pt-8 border-t border-white/5">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Your conversion engine is currently performing at <strong>{pipelineStats?.leadToCallRate || 0}%</strong> efficiency from lead to booked call. 
                      Focus on high-intent lead triggers to increase call volume.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedLead && selectedLead.outreach && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <motion.div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto glass-card border-accent/20 bg-background rounded-[2.5rem] shadow-2xl p-12 space-y-10">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[10px] uppercase font-bold text-accent">Lead Intelligence</p>
                <h3 className="text-3xl font-display font-bold">{selectedLead.name}</h3>
              </div>
              <button onClick={() => setSelectedLead(null)}><XCircle className="w-8 h-8 text-muted-foreground" /></button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-[10px] uppercase font-bold text-accent">AI Outreach Toolkit</p>
                <div className="glass-card p-6 rounded-2xl bg-white/[0.02]">
                  <p className="text-xs font-bold mb-2">High Intent Response:</p>
                  <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{selectedLead.outreach.highResponse}</p>
                  <Button className="w-full bg-accent text-background text-[10px] font-black" onClick={() => sendResponse(selectedLead.id, 'high', selectedLead.outreach?.highResponse || "")}>Dispatch Invite</Button>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-[10px] uppercase font-bold text-accent">Intelligence Summary</p>
                <div className="glass-card p-6 rounded-2xl bg-white/[0.02]">
                  <p className="text-xs text-muted-foreground italic leading-relaxed">"{selectedLead.aiSummary}"</p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-white/5 space-y-6">
              <p className="text-[10px] uppercase font-bold text-accent">Pipeline Management</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase text-muted-foreground font-bold pl-1">Stage Transition</label>
                  <select 
                    value={selectedLead.status}
                    onChange={(e) => updateLeadStatus(selectedLead.id, e.target.value, selectedLead.revenue)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-3 text-sm text-foreground outline-none hover:bg-white/[0.08] transition-all"
                  >
                    <option value="New">New Inquiry</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Replied">Replied (Engaged)</option>
                    <option value="Call Booked">Call Booked</option>
                    <option value="Call Completed">Call Completed</option>
                    <option value="Audit Sold">Audit Sold</option>
                    <option value="Upsell Closed">Upsell Closed</option>
                    <option value="Closed">Closed / Lost</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase text-muted-foreground font-bold pl-1">Deal Value (Revenue)</label>
                  <div className="relative">
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <input 
                      type="number"
                      defaultValue={selectedLead.revenue || 0}
                      onBlur={(e) => updateLeadStatus(selectedLead.id, selectedLead.status, Number(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-5 py-3 text-sm text-foreground outline-none focus:border-accent/40 transition-all"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-white/5 flex justify-end">
              <Button variant="outline" onClick={() => setSelectedLead(null)}>Close Suite</Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
